import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { franquiaService } from "../../services/franquiaService";
import { municipioService } from "../../services/enderecoService"; // Use municipioService instead of cidadeService
import { PaginatedList, Municipio } from "../../types/localidade";
import { Cidade } from "../../types/cidade";
import { Franquia } from "../../types/franquia";
import { useDebounce } from "../../hooks/useDebounce";
import { BairroSelectionModal } from "../../components/BairroSelectionModal";

const ufs = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

interface TerritorioSelecionado {
  cidade: Cidade;
  tipo: "INTEIRA" | "PARCIAL";
  bairros: Set<number>;
  bairrosNomes: Map<number, string>;
}

export const GerenciarAreasAtuacao: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const franquiaId = parseInt(id || "0");

  const [franquia, setFranquia] = useState<Franquia | null>(null);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [ufFilter, setUfFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasFiltered, setHasFiltered] = useState(false);

  const [selectedTerritorios, setSelectedTerritorios] = useState<
    Map<number, TerritorioSelecionado>
  >(new Map());
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    cidade: Cidade | null;
  }>({ isOpen: false, cidade: null });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const pageSize = 10;

  useEffect(() => {
    const fetchFranquiaData = async () => {
      if (!franquiaId) {
        setError("ID da franquia não fornecido.");
        setLoading(false);
        return;
      }
      try {
        const franquiaData = await franquiaService.buscarPorId(franquiaId);
        setFranquia(franquiaData);

        const initialTerritorios = new Map<number, TerritorioSelecionado>();

        // Verificar se area_atuacao existe
        if (franquiaData.area_atuacao) {
          const areaAtuacao = franquiaData.area_atuacao as any;

          // Processar municípios completos (compatibilidade com ambas estruturas)
          const municipiosCompletos =
            areaAtuacao.municipios_completos || areaAtuacao.municipios || [];
          municipiosCompletos.forEach((cidade: any) => {
            initialTerritorios.set(cidade.id, {
              cidade,
              tipo: "INTEIRA",
              bairros: new Set(),
              bairrosNomes: new Map(),
            });
          });

          // Processar regiões de município (parciais)
          const regiaoMunicipio = areaAtuacao.regiao_municipio || [];
          regiaoMunicipio.forEach((regiao: any) => {
            const cidade = regiao.municipio;
            const bairroIds = new Set<number>(
              regiao.bairros.map((b: any) => b.id as number)
            );
            const bairroNomes = new Map<number, string>(
              regiao.bairros.map((b: any) => [
                b.id as number,
                (b.nome_original || b.nome) as string,
              ])
            );

            initialTerritorios.set(cidade.id, {
              cidade,
              tipo: "PARCIAL",
              bairros: bairroIds,
              bairrosNomes: bairroNomes,
            });
          });

          // Se a API retornou "bairros" diretamente (nova estrutura)
          const bairrosDireto = areaAtuacao.bairros || [];
          if (bairrosDireto.length > 0) {
            // Agrupar bairros por município
            const bairrosPorMunicipio = new Map<number, any[]>();
            const bairrosInvalidos: any[] = [];

            bairrosDireto.forEach((bairro: any) => {
              const municipioId = bairro.municipio.id;

              // Validar se o bairro realmente pertence ao município declarado
              // Comparando o ID do município no objeto bairro com o declarado
              if (bairro.municipio?.id === municipioId) {
                if (!bairrosPorMunicipio.has(municipioId)) {
                  bairrosPorMunicipio.set(municipioId, []);
                }
                bairrosPorMunicipio.get(municipioId)!.push(bairro);
              } else {
                bairrosInvalidos.push(bairro);
                console.warn(
                  `⚠️ Bairro ${bairro.nome_original || bairro.nome} (ID: ${
                    bairro.id
                  }) ` + `não pertence ao município ${municipioId}. Ignorado.`
                );
              }
            });

            if (bairrosInvalidos.length > 0) {
              console.warn(
                `⚠️ ${bairrosInvalidos.length} bairro(s) foram ignorados por inconsistência de município.`,
                bairrosInvalidos
              );
            }

            // Criar territórios parciais a partir dos bairros agrupados
            bairrosPorMunicipio.forEach((bairros, municipioId) => {
              const cidade = bairros[0].municipio;
              const bairroIds = new Set<number>(
                bairros.map((b: any) => b.id as number)
              );
              const bairroNomes = new Map<number, string>(
                bairros.map((b: any) => [
                  b.id as number,
                  (b.nome_original || b.nome) as string,
                ])
              );

              initialTerritorios.set(municipioId, {
                cidade,
                tipo: "PARCIAL",
                bairros: bairroIds,
                bairrosNomes: bairroNomes,
              });
            });
          }
        }

        setSelectedTerritorios(initialTerritorios);
      } catch (err) {
        console.error("Erro ao carregar dados da franquia:", err);
        setError("Não foi possível carregar os dados da franquia.");
      } finally {
        setLoading(false);
      }
    };

    fetchFranquiaData();
  }, [franquiaId]);

  useEffect(() => {
    if (!hasFiltered) return;

    const fetchCidades = async () => {
      setLoading(true);
      try {
        const response = await municipioService.listar({
          search: debouncedSearchTerm
            ? debouncedSearchTerm.toLowerCase()
            : undefined,
          ...(ufFilter && { uf: ufFilter }),
          limit: 5000,
        });
        const cidadesData: Cidade[] = response.map((municipio: Municipio) => ({
          ...municipio,
          estado: municipio.uf.nome,
          pais: "Brasil",
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
        }));
        setCidades(cidadesData);
        setTotalCount(response.length);
      } catch (err) {
        console.error("Erro ao carregar cidades:", err);
        setError("Não foi possível carregar a lista de cidades.");
      } finally {
        setLoading(false);
      }
    };

    fetchCidades();
  }, [debouncedSearchTerm, ufFilter, hasFiltered]);

  const paginatedCidades = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return cidades.slice(start, end);
  }, [cidades, currentPage, pageSize]);

  const handleSelectCidade = (cidade: Cidade) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(cidade.id)) {
        newMap.delete(cidade.id);
      } else {
        newMap.set(cidade.id, {
          cidade,
          tipo: "INTEIRA",
          bairros: new Set(),
          bairrosNomes: new Map(),
        });
      }
      return newMap;
    });
  };

  const handleTerritorioTypeChange = (
    cidadeId: number,
    tipo: "INTEIRA" | "PARCIAL"
  ) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      const territorio = newMap.get(cidadeId);
      if (territorio) {
        territorio.tipo = tipo;
        if (tipo === "INTEIRA") {
          territorio.bairros.clear();
          territorio.bairrosNomes.clear();
        }
      }
      return newMap;
    });
  };

  const handleOpenBairroModal = (cidade: Cidade) => {
    setModalState({ isOpen: true, cidade });
  };

  const handleCloseBairroModal = () => {
    setModalState({ isOpen: false, cidade: null });
  };

  const handleSaveBairros = (
    cidadeId: number,
    bairros: Set<number>,
    bairrosNomes: Map<number, string>
  ) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      const territorio = newMap.get(cidadeId);
      if (territorio) {
        territorio.bairros = bairros;
        territorio.bairrosNomes = bairrosNomes;
      }
      return newMap;
    });
  };

  const handleSave = async () => {
    if (!franquiaId) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    const municipios_completos: number[] = [];
    const regiao_municipio: { id_municipio: number; bairros: number[] }[] = [];

    selectedTerritorios.forEach((territorio) => {
      if (territorio.tipo === "INTEIRA") {
        municipios_completos.push(territorio.cidade.id);
      } else {
        if (territorio.bairros.size > 0) {
          regiao_municipio.push({
            id_municipio: territorio.cidade.id,
            bairros: Array.from(territorio.bairros),
          });
        }
      }
    });

    // Log dos dados que serão enviados para debug
    console.log("📤 Enviando dados para API:", {
      municipios_completos,
      regiao_municipio,
    });

    try {
      await franquiaService.atualizarParcial(franquiaId, {
        area_atuacao: {
          municipios_completos,
          regiao_municipio,
        },
      });
      setSuccess("Áreas de atuação atualizadas com sucesso!");
      setTimeout(() => navigate(`/franquias/${franquiaId}/detalhes`), 2000);
    } catch (err: any) {
      console.error("❌ Erro ao salvar áreas de atuação:", err);

      // Extrair mensagem de erro mais detalhada
      let errorMessage = "Ocorreu um erro ao salvar as alterações.";

      if (err.response?.data) {
        const errorData = err.response.data;

        // Se há erros específicos de area_atuacao
        if (errorData.area_atuacao) {
          const areaErros = errorData.area_atuacao;

          if (
            areaErros.non_field_errors &&
            Array.isArray(areaErros.non_field_errors)
          ) {
            errorMessage = areaErros.non_field_errors.join(" ");
          } else if (typeof areaErros === "string") {
            errorMessage = areaErros;
          } else {
            errorMessage = JSON.stringify(areaErros);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      }

      console.error("📋 Detalhes do erro:", {
        status: err.response?.status,
        data: err.response?.data,
        message: errorMessage,
      });

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && !franquia) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32bef0]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {modalState.isOpen && modalState.cidade && (
        <BairroSelectionModal
          cidade={modalState.cidade}
          isOpen={modalState.isOpen}
          onClose={handleCloseBairroModal}
          initialSelectedBairros={
            selectedTerritorios.get(modalState.cidade.id)?.bairros || new Set()
          }
          onSave={(bairros, bairrosNomes) =>
            handleSaveBairros(modalState.cidade!.id, bairros, bairrosNomes)
          }
        />
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#29306a]">
          Gerenciar Áreas de Atuação
        </h1>
        <p className="mt-2 text-sm text-[#64748b]">
          Selecione ou modifique as cidades e bairros de atuação para a franquia{" "}
          <strong>{franquia?.nome_fantasia}</strong>.
        </p>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 text-green-700 p-3 rounded-md mb-4">
          {success}
        </div>
      )}

      <div className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-4 text-[#29306a]">
            Buscar e Adicionar Cidades
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome da cidade..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32bef0]"
            />
            <select
              value={ufFilter}
              onChange={(e) => setUfFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32bef0]"
            >
              <option value="">Todos os Estados</option>
              {ufs.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
            <button
              onClick={() => setHasFiltered(true)}
              className="w-full md:w-auto justify-center rounded-md border border-transparent bg-[#32bef0] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#2ba8d8] focus:outline-none focus:ring-2 focus:ring-[#32bef0] focus:ring-offset-2"
            >
              Buscar
            </button>
          </div>

          {!hasFiltered ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-500">
                Utilize os filtros para buscar e adicionar cidades à área de
                atuação.
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32bef0]"></div>
            </div>
          ) : (
            <div className="overflow-hidden border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-12"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      UF
                    </th>
                  </tr>
                </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                  {paginatedCidades.map((cidade) => (
                                    <tr key={cidade.id} className="hover:bg-gray-50">
                                      <td className="pl-4">
                                        <input
                                          type="checkbox"
                                          checked={selectedTerritorios.has(cidade.id)}
                                          onChange={() => handleSelectCidade(cidade)}
                                          className="h-4 w-4 text-[#32bef0] focus:ring-[#2ba8d8] border-gray-300 rounded"
                                        />
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {cidade.nome_original}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {cidade.uf.sigla}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <p className="text-sm text-gray-700">
                Página <span className="font-medium">{currentPage}</span> de{" "}
                <span className="font-medium">{totalPages}</span>
              </p>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedTerritorios.size > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#29306a]">
              Territórios Selecionados ({selectedTerritorios.size})
            </h3>
            <ul className="mt-4 space-y-4">
              {Array.from(selectedTerritorios.values()).map(
                ({ cidade, tipo, bairros, bairrosNomes }) => (
                  <li
                    key={cidade.id}
                    className="p-4 border rounded-md bg-gray-50"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800">
                        {cidade.nome_original} - {cidade.uf.sigla}
                      </span>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`tipo-${cidade.id}`}
                            checked={tipo === "INTEIRA"}
                            onChange={() =>
                              handleTerritorioTypeChange(cidade.id, "INTEIRA")
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm">Cidade Inteira</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`tipo-${cidade.id}`}
                            checked={tipo === "PARCIAL"}
                            onChange={() =>
                              handleTerritorioTypeChange(cidade.id, "PARCIAL")
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm">
                            Parcial (Bairros)
                          </span>
                        </label>
                      </div>
                    </div>
                    {tipo === "PARCIAL" && (
                      <div className="mt-4 pl-2 border-l-2 border-blue-200">
                        <button
                          onClick={() => handleOpenBairroModal(cidade)}
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          Gerenciar Bairros ({bairros.size} selecionados)
                        </button>
                        {bairros.size > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-gray-600 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                              {Array.from(bairrosNomes.entries()).map(
                                ([id, nome]) => (
                                  <span
                                    key={id}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center justify-between"
                                    title={`ID: ${id}`}
                                  >
                                    {nome}
                                    <span className="ml-1 text-xs text-blue-600">
                                      #{id}
                                    </span>
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                )
              )}
            </ul>
          </div>
        )}

        <div className="flex justify-end mt-8 space-x-4">
          <Link
            to={`/franquias/${franquiaId}/detalhes`}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Cancelar
          </Link>
          <button
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? "Salvando..." : "Salvar Alterações"}
          </button>
        </div>
      </div>
    </div>
  );
};
